"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "@/hooks/use-toast";
import {
  PersonalDataSection,
  AddressSection
} from "@/features/applications/components";

import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { ProfileStepsLayout, Step } from "../components/ProfileStepsLayout";

interface ApplicationData {
  given_name?: string;
  family_name?: string;
  email?: string;
  [key: string]: any;
}

interface TalentPersonalInfoProps {
  currentStep: number;
  totalSteps: number;
}

export default function TalentPersonalInfo({
  currentStep,
  totalSteps
}: TalentPersonalInfoProps) {
  const { t } = useTranslation(["talent", "common"]);
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<ApplicationData>({});
  const [initialData, setInitialData] = useState<ApplicationData>({});

  // Step information
  const stepTitle = t("talent:layout.progress_steps.personal_info");
  const stepDescription = t("talent:personal_info.form_description");
  const estimatedTime = t("talent:application.estimated_times.10_minutes");

  useEffect(() => {
    const fetchApplicationData = async () => {
      if (profile.anonymous) return;

      try {
        setLoading(true);
        const { data } = await supabase
          .from("talent_profiles")
          .select("*")
          .eq("id", profile.id)
          .single();
        if (data) {
          setFormData(data);
          setInitialData(data);
        }
      } catch (error) {
        console.error("Error fetching application data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchApplicationData();
  }, [profile.id, profile.anonymous]);

  const getStepStatus = (
    stepId: string
  ): "completed" | "current" | "upcoming" => {
    if (stepId === "personal_info") return "current";
    if (
      stepId === "german_skills" ||
      stepId === "experience" ||
      stepId === "education"
    )
      return "upcoming";
    return "completed";
  };

  const steps: Step[] = [
    {
      id: "personal_info",
      title: t("talent:layout.progress_steps.personal_info"),
      href: "/profile/personal-info",
      status: getStepStatus("personal_info")
    },
    {
      id: "german_skills",
      title: t("talent:dashboard.progress.steps.german_skills"),
      href: "/profile/german-skills",
      status: getStepStatus("german_skills")
    },
    {
      id: "experience",
      title: t("talent:dashboard.progress.steps.experience"),
      href: "/profile/experience",
      status: getStepStatus("experience")
    },
    {
      id: "education",
      title: t("talent:dashboard.progress.steps.education"),
      href: "/profile/education",
      status: getStepStatus("education")
    }
  ];

  const validateForm = () => {
    return Boolean(
      formData.given_name && formData.family_name && formData.email
      // && formData.date_of_birth
    );
  };

  const handleNext = async () => {
    if (!validateForm()) {
      toast({
        title: t("personal_info.validation_error"),
        description: "Bitte füllen Sie alle erforderlichen Felder aus",
        variant: "destructive"
      });
      return;
    }

    if (JSON.stringify(formData) === JSON.stringify(initialData)) {
      navigate("/profile/german-skills");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase
        .from("talent_profiles")
        .update({
          ...formData,
          updated_at: new Date().toISOString()
        })
        .eq("id", profile.id);
      if (error) throw error;

      toast({
        title: t("personal_info.save_success"),
        description: "Persönliche Daten erfolgreich gespeichert"
      });

      navigate("/profile/german-skills");
    } catch (error) {
      console.error("Error saving application:", error);
      toast({
        title: t("personal_info.save_error"),
        description: "Fehler beim Speichern",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProfileStepsLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={stepTitle}
      description={stepDescription}
      estimatedTime={estimatedTime}
      steps={steps}
    >
      <div className="space-y-8">
        <PersonalDataSection
          uploading={loading}
          loading={loading}
          profile={formData}
          setProfile={setFormData}
          readOnly={false}
        />

        <AddressSection
          loading={loading}
          profile={formData}
          setProfile={setFormData}
          readOnly={false}
        />

        {/* Navigation */}
        <div className="flex justify-end pt-6 border-t">
          <Button
            id="next-button"
            onClick={handleNext}
            disabled={loading || !validateForm()}
            className="flex items-center gap-2"
          >
            {loading ? t("common:status.saving") : t("common:buttons.next")}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </ProfileStepsLayout>
  );
}
